import { Text } from "@mantine/core";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";

import { DriverLog, LogOperationTypes } from "@/domain";
import { dateRangeQueryParamsSchema, useDriverLogsQueryParams } from "@/features/app/logs/hooks";
import { useSession } from "@/hooks/@custom/useSession";
import { useListLogDriverInfiniteQuery } from "@/store/apis/nextcargo/log-driver";

type LogMessage = {
  operation: string;
  created_at: Date;
  content?: string;
  contentPosition?: "center" | "end";
};

const typeLogMessageMap: Record<LogOperationTypes, string> = {
  LOGIN: "Logou no sistema",
  IN_QUEUE: "Entrou na Fila",
  OUT_QUEUE: "Saiu da Fila",
  ATT_QUEUE: "Atendido pela expedição",
  FAU_QUEUE: "Faltoso pela expedição",
  TOL_QUEUE: "Faltoso por tolerância",
  REG_DRIVER: "Resgistro Motorista",
  CAD_DRIVER: "Cadastro Motorista",
  CAD_TRUCK: "Cadastrou Caminhão",
  EDIT_TRUCK: "Editou Caminhão",
  BIND_TRUNCK: "Vinculou Caminhão",
  UNBIND_TRUCK: "Desvinculou Caminhão",
  IN_ORDER: "Adicionado no Pedido",
  OUT_COMPANY: "Mudou de empresa",
  DEL_ADM: "Excluiu do Sistema /Admin",
  DEL_APP: "Excluiu do Sistema /App",
  READ_POPUP: "Visualizou Popup",
  CHECK_IN: "fez check-in",
  CHECK_OUT: "cancelou check-in"
} as const;

export const Route = createFileRoute("/app/logs/historico")({
  component: RouteComponent,
  validateSearch: dateRangeQueryParamsSchema
});

function RouteComponent() {
  const [query] = useDriverLogsQueryParams();

  const { driver } = useSession();
  const { data: list } = useListLogDriverInfiniteQuery(
    {
      id: Number(driver?.id),
      startDate: query?.dateRange?.[0],
      endDate: query?.dateRange?.[1]
    },
    {
      refetchOnMountOrArgChange: true
    }
  );
  const data = list?.pages.flatMap((item) => item.data);
  const rows = formatLogs(data);

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: "id",
      headerName: "Id",
      width: 60,
      headerAlign: "center",
      align: "center",
      resizable: false
    },
    {
      field: "title",
      headerName: "Título",
      flex: 1,
      display: "flex",
      renderCell: (params) => {
        return <Text>{params.row.title}</Text>;
      }
    },
    {
      field: "company",
      headerName: "Empresa",
      align: "center",
      headerAlign: "center",
      display: "flex",
      renderCell: (params) => {
        return <Text>{params.row.company.name}</Text>;
      }
    }
  ];

  function checkType(key: LogOperationTypes) {
    return typeLogMessageMap[key] || "Logou no sistema";
  }

  function handleLogMessage({ operation, created_at, content, contentPosition = "end" }: LogMessage) {
    let message = "";

    const logDate = dayjs(created_at).format("DD/MM/YYYY [às] HH:mm");

    if (content) {
      if (contentPosition === "center") {
        message = `Motorista ${operation} ${content} ${logDate}`;
      } else {
        message = `Motorista ${operation} ${logDate} ${content}`;
      }
    } else {
      message = `Motorista ${operation} ${logDate}`;
    }

    return message;
  }

  function formatLogs(logs: DriverLog[] = []) {
    return logs.map((item, index) => {
      // item.id = index;
      const keys = Object.keys(item.data);
      const values = Object.values(item.data);

      const operationType = item.operation_type;
      const operation = checkType(operationType);

      const date = dayjs(item.createdAt).toDate();
      const details = [];

      let title = handleLogMessage({
        created_at: date,
        operation
      });

      if (operationType === "IN_QUEUE") {
        const licensePlate = item.data?.truck?.licensePlate;
        const existsLicensePlate = licensePlate;

        const content = existsLicensePlate ? `com a Placa ${licensePlate}` : `como Autônomo`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content
        });
      }

      if (operationType === "IN_ORDER") {
        const code = String(item.data?.scheduling?.order?.code);

        title = handleLogMessage({
          created_at: date,
          operation,
          content: code,
          contentPosition: "center"
        });
      }

      if (operationType === "OUT_COMPANY") {
        const content = `Para ${item.data?.companyTo?.company}`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content
        });
      }

      if (operationType === "CAD_TRUCK") {
        const content = `Caminhão Placa ${item.data?.truck?.licensePlate}`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content
        });
      }

      if (operationType === "EDIT_TRUCK") {
        const content = `Caminhão Placa ${item.data?.truck?.licensePlate}`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content
        });
      }

      if (operationType === "UNBIND_TRUCK") {
        const content = `Caminhão Placa ${item.data?.truck?.licensePlate}`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content
        });
      }

      if (operationType === "BIND_TRUNCK") {
        const content = `Caminhão Placa ${item.data?.truck?.licensePlate}`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content
        });
      }

      if (operationType === "BIND_TRUNCK") {
        const content = `Caminhão Placa ${item.data?.truck?.licensePlate}`;

        title = handleLogMessage({
          created_at: date,
          operation,
          content
        });
      }

      if (operationType === "READ_POPUP") {
        const popupData = item.data.popup;

        const popupTitle = popupData.title;
        const popupLabel = popupData.label;

        const popupTitleIsEmpty = popupTitle === "";
        const content = !popupTitleIsEmpty ? popupTitle : popupLabel;

        title = handleLogMessage({
          operation,
          created_at: date,
          content,
          contentPosition: "center"
        });
      }

      if (operationType === "CHECK_IN") {
        title = handleLogMessage({
          operation,
          content: "em",
          contentPosition: "center",
          created_at: date
        });
      }

      if (operationType === "CHECK_OUT") {
        title = handleLogMessage({
          operation,
          content: "em",
          contentPosition: "center",
          created_at: date
        });
      }

      for (let index = 0; index < keys.length; index++) {
        details.push({ key: keys[index], value: values[index] });
      }

      const companyId = item.data?.company?.id;
      const companyName = item.data?.company?.company;

      const data = {
        id: index + 1,
        title: title,
        company: { id: companyId, name: companyName },
        details: details,
        created_at: date.toLocaleDateString()
      };

      return data;
    });
  }

  return (
    <DataGrid
      rows={rows}
      rowHeight={80}
      columns={columns}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 8 }
        },
        density: "compact"
      }}
      pageSizeOptions={[10]}
      disableColumnMenu
      disableRowSelectionOnClick
      sx={{ marginBottom: "20px" }}
      slots={{
        noResultsOverlay: () => <p>Registro não encontrado</p>,
        noRowsOverlay: () => (
          <div className="flex h-full items-center justify-center">
            <p>Não há registros no momento</p>
          </div>
        )
      }}
    />
  );
}
